import type { DifficultyId, WaveDefinition } from '../content/types';
import type { ResolvedWaveMetadata, RunMode } from './types';

const HP_PER_COMPLETED_CYCLE: Readonly<Record<DifficultyId, number>> = {
  easy: 0.75,
  normal: 1,
  hard: 1.5,
};

export interface ResolvedWave extends ResolvedWaveMetadata {
  definition: WaveDefinition;
}

export const infiniteHealthMultiplier = (difficultyId: DifficultyId, completedCycles: number): number =>
  1 + Math.max(0, completedCycles) * HP_PER_COMPLETED_CYCLE[difficultyId];

export const scaleEnemyHealth = (baseHealth: number, multiplier: number): number =>
  Math.round(baseHealth * multiplier);

export const completedCyclesForClearedWaves = (completedWaves: number, cycleLength: number): number =>
  Math.floor(completedWaves / cycleLength);

/**
 * Resolves one absolute 1-based attack without extending the authored script.
 * Fixed mode returns null beyond the script; Infinite wraps over that script.
 */
export const resolveWave = (
  script: readonly WaveDefinition[],
  difficultyId: DifficultyId,
  runMode: RunMode,
  absoluteWave: number,
): ResolvedWave | null => {
  const cycleLength = script.length;
  if (cycleLength === 0 || !Number.isInteger(absoluteWave) || absoluteWave < 1) return null;
  if (runMode === 'fixed' && absoluteWave > cycleLength) return null;
  const completedCycles = runMode === 'infinite' ? Math.floor((absoluteWave - 1) / cycleLength) : 0;
  const baseWave = ((absoluteWave - 1) % cycleLength) + 1;
  const hpMultiplier = runMode === 'infinite' ? infiniteHealthMultiplier(difficultyId, completedCycles) : 1;
  return {
    definition: script[baseWave - 1],
    absoluteWave,
    baseWave,
    completedCycles,
    cycle: completedCycles + 1,
    cycleLength,
    hpMultiplier,
    hpPercent: Math.round(hpMultiplier * 100),
  };
};

export const waveMetadata = (resolved: ResolvedWave): ResolvedWaveMetadata => ({
  absoluteWave: resolved.absoluteWave,
  baseWave: resolved.baseWave,
  completedCycles: resolved.completedCycles,
  cycle: resolved.cycle,
  cycleLength: resolved.cycleLength,
  hpMultiplier: resolved.hpMultiplier,
  hpPercent: resolved.hpPercent,
});
